import React from "react";
import {
	Box,
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogContentText,
	DialogTitle,
	Typography,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import DeleteOutlineTwoToneIcon from "@mui/icons-material/DeleteOutlineTwoTone";
import { useApiMutation } from "src/hooks/useApiMutation";
import type { IGiftItem } from "./type";

interface Props {
	open: boolean;
	handleClose: () => void;
	gift: IGiftItem | null;
}

const DeleteGiftModal: React.FC<Props> = ({ open, handleClose, gift }) => {
	const giftId = gift?.id;

	const { mutate: deleteGift, isPending } = useApiMutation<void, void>({
		method: "DELETE",
		endPoint: `/api/v1/admin/gift/${giftId || 0}`,
		invalidateQueries: ["Gift"],
	});

	const handleDelete = () => {
		if (!giftId) return;
		deleteGift(
			{
				dynamicEndpoint: `/api/v1/admin/gift/${giftId}`,
			} as any,
			{
				onSuccess: () => {
					handleClose();
				},
			}
		);
	};

	const onDialogClose = () => {
		if (isPending) return;
		handleClose();
	};

	const visitorDisplayName = gift?.visitor_name || "this visitor";

	return (
		<Dialog
			open={open}
			onClose={onDialogClose}
			maxWidth="xs"
			fullWidth
			disableEnforceFocus
		>
			<DialogTitle sx={{ textAlign: "center", pt: 3 }}>
				<Box
					sx={{
						width: 56,
						height: 56,
						borderRadius: "50%",
						backgroundColor: "rgba(211, 47, 47, 0.1)",
						display: "flex",
						alignItems: "center",
						justifyContent: "center",
						margin: "0 auto 12px auto",
						color: "error.main",
					}}
				>
					<DeleteOutlineTwoToneIcon sx={{ fontSize: 32 }} />
				</Box>
				<Typography variant="h5" fontWeight={700}>
					Delete Gift Record?
				</Typography>
			</DialogTitle>

			<DialogContent>
				<DialogContentText sx={{ textAlign: "center", color: "text.secondary" }}>
					{`Are you sure you want to delete the gift record #${giftId || ""} for ${visitorDisplayName}${
						gift?.visitor_no ? ` (${gift.visitor_no})` : ""
					}? This action cannot be undone.`}
				</DialogContentText>
			</DialogContent>

			<DialogActions sx={{ p: 2.5, justifyContent: "center", gap: 1.5 }}>
				<Button
					onClick={onDialogClose}
					disabled={isPending}
					variant="outlined"
					color="inherit"
				>
					<span>Cancel</span>
				</Button>
				<LoadingButton
					onClick={handleDelete}
					loading={isPending}
					variant="contained"
					color="error"
				>
					<span>Yes, Delete</span>
				</LoadingButton>
			</DialogActions>
		</Dialog>
	);
};

export default DeleteGiftModal;
